import { Injectable, Output, EventEmitter, Directive } from '@angular/core';
import { Observable, Subscription, of, BehaviorSubject } from 'rxjs';
import { AppConfig } from '../../../../app.config';
import { HttpClient } from '@angular/common/http';
import { catchError, pluck } from 'rxjs/operators';
import { NavigationLink } from '../../models/worksite.model';

/**
 * Worksite object returned by the API
 */
export class WorksiteBasicInfo {
    Id: string;
    IwmId: number;
    Identifier: string;
    Name: string;
    Description: string;
    PlanId: string;
    OwnerCompanyId: string;
    OwnerCompanyName: string;
    Archived: boolean;
    UnitsLocked: boolean;
    UnitLockStateChangerPerson: string;
    UnitLockStateChangerPersonName: string;
    UnitLockStateChanged: string;
    IncludedInTaxReporting: boolean;
    PublishToEndCustomer: boolean;
    BudgetedValue?: number;
    BusinessDomain: string;
    Subtype: string;
    WorksiteStatus: string;
    Priority: string;
    PlannedStarted: string;
    ActualStarted: string;
    PlannedWorkCompleted: string;
    ActualWorkCompleted: string;
    WorksiteNumber: string;
    LastModifiedTime?: string;
    Created?: string;
    LastModifierPersonFullName: string;
    MassApprovalChanged: string;
    MassApprovalChangerPerson: string;
    MassApprovalChangerPersonName: string;
    MassApprovalDone: boolean;
}

/**
 * Unit lock state
 */
export class UnitLockState {
    public Id: string;
    public WorksiteId: string;
    public CompanyId: string;
    public Locked: boolean;
    public ChangerPerson: string;
    public ChangerPersonName: string;
    public Changed: string;
}

@Directive()
@Injectable({
    providedIn: 'root'
})
export class WorksiteBasicinfoService {

    private urlIwmBackend: string = this.config.backendIwmUri + 'WorksiteApi/';
    private urlBasicinfoSearch: string = 'Worksites?$filter=IwmId eq {0}';
    private urlBasicinfo: string = 'Worksites({0})';
    private urlPlan: string = this.config.backendIwmUri + 'PlanApi/';
    private urlPortalBackend: string = this.config.backendPortalUri + 'WebAPI/api/';


    public basicInfochanged$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public streamSubscription: Subscription = new Subscription();

    // Current worksite basic information object as subscribable stream
    // public worksiteBasicInformation$: BehaviorSubject<WorksiteBasicInfo> = new BehaviorSubject<WorksiteBasicInfo>(new WorksiteBasicInfo());

    // Define new worksiteChanged custom event
    @Output()
    worksiteChanged: EventEmitter<string> = new EventEmitter<string>();


    constructor(private http: HttpClient, private config: AppConfig) { }

    /**
      * Subscribe to get the latest worksite basic information data
      * @param observable Observable TBI
      */
    /* private subscribeToWorksiteBasicInformation(observable: Observable<WorksiteBasicInfo>) {
       this.streamSubscription.unsubscribe();
       this.streamSubscription = observable.subscribe((res) => {
         this.worksiteBasicInformation$.next(res)
       });
     }*/


    /**
       * Get worksite details by GUID
       * @param {string} id GUID worksite identifier
       * @returns {Observable<WorksiteBasicInfo>}
       */
    public getWorksiteBasicinfo(id: string, idGuard: boolean = false): Observable<WorksiteBasicInfo> {
        const url = this.urlIwmBackend + this.urlBasicinfo.replace('{0}', id);
        const worksite = this.fetchData(url);
        //  if (!idGuard) { this.subscribeToWorksiteBasicInformation(worksite); }
        return worksite;
    }

    /**
     * Get worksite details by iwmId
     * @param {number} iwmId numeric worksite identifier
     * @returns {Observable<WorksiteBasicInfo>}
     */
    public getWorksiteBasicInfoById(iwmId: number): Observable<WorksiteBasicInfo> {
        const url = this.urlIwmBackend + this.urlBasicinfoSearch.replace('{0}', iwmId.toString());
        const data: Observable<WorksiteBasicInfo> = <Observable<WorksiteBasicInfo>>this.fetchData(url);
        return data;
    }

    /**
      * Update worksite
      * @param {string} worksiteId Id of the worksite
      * @param {WorksiteBasicInfo} worksiteBasicInfo WorksiteBasicInfo object
      * @returns {Observable<boolean>}
      */
    public update(worksiteId: string, worksiteBasicInfo: WorksiteBasicInfo): Observable<any> {
        const url = this.urlIwmBackend + this.urlBasicinfo.replace('{0}', worksiteId);
        return this.http.patch(url, worksiteBasicInfo);
    }

    public getBudgetHandlers(): Observable<any> {
        const url = this.config.backendIwmUri + 'Api/Tasks/GetBudgetHandlers';
        return this.fetchData(url);
    }

    /**
     * Update worksite
     * @param {string} worksiteId Id of the worksite
     * @param {WorksiteBasicInfo} worksiteBasicInfo WorksiteBasicInfo object
     * @returns {Observable<boolean>}
     */
    public updateWorksiteNumber(worksiteId: string, value: string): Observable<any> {
        const url = this.urlIwmBackend + this.urlBasicinfo.replace('{0}', worksiteId) + '/Default.SetWorksiteNumber';
        let body = { Value: value };
        return this.http.post(url, body);
    }

    /**
     * Archive worksite (and catch possible error)
     * @param {string} worksiteId WorksiteId
     */
    public archiveWorksite(worksiteId: string): Observable<any> {
        const url = this.urlIwmBackend + this.urlBasicinfo.replace('{0}', worksiteId) + '/Default.Archive';
        const resp = this.http.post(url, {})
            .pipe(catchError(e => of(e)))
        return resp;
    }

    /**
  * Batch archive tasks
  * @param {Array<string>} tasks array of taskIds
  */
    public batchArchiveWorksites(worksites: Array<string>): Observable<Array<string>> {
        const url = this.urlIwmBackend + 'Worksites/Default.BatchArchive';
        return this.http.post<Array<string>>(url, { Worksites: worksites })
            .pipe(pluck('value') as any);
    }

    /**
    * Restore worksite (and catch possible error)
    * @param {string} worksiteId WorksiteId
    */
    public restoreWorksite(worksiteId: string): Observable<any> {
        const url = this.urlIwmBackend + this.urlBasicinfo.replace('{0}', worksiteId) + '/Default.Restore';
        const resp = this.http.post(url, {})
            .pipe(catchError(e => of(e)));
        return resp;
    }

    /**
  * Batch restore tasks from archive
  * @param {Array<string>} tasks array of taskIds
  */
    public batchRestoreWorksites(worksites: Array<string>): Observable<Array<string>> {
        const url = this.urlIwmBackend + 'Worksites/Default.BatchRestore';
        return this.http.post<Array<string>>(url, { Worksites: worksites })
            .pipe(pluck('value') as any);
    }

    public handlePlanWorksiteRelation(worksiteId: string, planId: string): Observable<any> {
        let url = this.urlIwmBackend + this.urlBasicinfo.replace('{0}', worksiteId) + '/Default.HandlePlanWorksiteRelation';
        let body = { planId: planId };
        return this.http.post(url, body);
    }


    /**
     * Updates budget unit lock state.
     *
     * @param {string} worksiteId worksiteId
     * @param {boolean} taskBasicInfo Flag indicating if budget units should be locked or not
     * @returns {Observable<BudgetUnitLockState>}
     */
    public updateBudgetUnitLock(worksiteId: string, locked: boolean): Observable<UnitLockState> {
        const url = this.urlIwmBackend + this.urlBasicinfo.replace('{0}', worksiteId) + '/Default.SetUnitLockState';

        return this.http.post<UnitLockState>(url, {
            Locked: locked
        });
    }
    public updateTasksBudgetUnitLock(worksiteId: string, locked: boolean): Observable<any> {
        const url = this.urlIwmBackend + this.urlBasicinfo.replace('{0}', worksiteId) + '/ApproveWorksiteAndTasksBudgets';
        return this.http.post<UnitLockState>(url, {
            Locked: locked
        });
    }

    /**
     * Adds worksite to tax reporting
     * @param {string} worksiteId WorksiteId
     */
    public addToReporting(worksiteId: string): Observable<any> {
        const url = this.urlIwmBackend + this.urlBasicinfo.replace('{0}', worksiteId) + '/Default.IncludeInTaxReporting';
        const resp = this.http.post(url, {})
            .pipe(catchError(e => of(e)))
        return resp;
    }

    /**
     * Removes worksite from tax reporting
     * @param {string} worksiteId WorksiteId
     */
    public removeFromReporting(worksiteId: string): Observable<any> {
        const url = this.urlIwmBackend + this.urlBasicinfo.replace('{0}', worksiteId) + '/Default.RemoveFromTaxReporting';
        const resp = this.http.post(url, {})
            .pipe(catchError(e => of(e)))
        return resp;
    }

    /**
    * Publish worksite to end customer
    * @param {string} worksiteId TaskId
    */
    public publishToEndCustomer(worksiteId: string): Observable<any> {
        const url = this.urlIwmBackend + this.urlBasicinfo.replace('{0}', worksiteId) + '/Default.PublishWorksiteToEndCustomer';
        const resp = this.http.post(url, {})
            .pipe(catchError(e => of(e)))
        return resp;
    }

    /**
     * Unpublish worksite from end customer
     * @param {string} worksiteId TaskId
     */
    public unpublishWorksiteFromEndCustomer(worksiteId: string): Observable<any> {
        const url = this.urlIwmBackend + this.urlBasicinfo.replace('{0}', worksiteId) + '/Default.UnpublishWorksiteFromEndCustomer';
        const resp = this.http.post(url, {})
            .pipe(catchError(e => of(e)))
        return resp;
    }


    public queryWorksites(query: string): Observable<string> {
        this.config.backendIwmUri + 'Api/'
        const url = `${this.config.backendIwmUri}WorksiteApi/Worksites?` + query;
        return this.http
            .get<any>(url);
    }


    /**
     * Get navigation link name for Plan
     * @param id
     * @returns
     */
    public GetNavigationLink(id: string): Observable<NavigationLink> {
        const url = `${this.urlPlan}/Plans(${id})?$select=IwmId,Identifier,Name`;
        return this.http.get<NavigationLink>(url)
    }


    /**
     * Fetch data from oData endpoints
     * @param {string} url Resource url
     * @returns {Observable<any>}
     */
    private fetchData(url: string): Observable<any> {
        return this.http.get(url);
    }

}
